import {
  procurementStageOf,
  type PurchaseRequest,
} from "../../model";
import type {
  useApprovalsWorkspace,
} from "./useApprovalsWorkspace";

type ApprovalsWorkspace = ReturnType<typeof useApprovalsWorkspace>;
export type ApprovalRowInput = Pick<
  ApprovalsWorkspace,
  "user" | "expandedRequestId" | "resolved" | "gmFinalOfferSelections" |
  "gmFinalDecisionNotes" | "evaluations" | "assignedApprovalRole"
>;

export function createApprovalRowModel<T extends ApprovalRowInput>(model: T, request: PurchaseRequest) {
  const {
    user,
    expandedRequestId,
    resolved,
    gmFinalOfferSelections,
    gmFinalDecisionNotes,
    evaluations,
    assignedApprovalRole,
  } = model;
  const resolution = resolved[request.id];
  const isActionable =
    request.pendingApprovalRole === assignedApprovalRole;
  const evaluation = evaluations[request.id];
  const systemOffer = evaluation?.offers.find(
    (offer) => offer.id === evaluation.topOfferId,
  );
  const selectedOffer = evaluation?.offers.find(
    (offer) =>
      offer.id ===
      (evaluation.recommendation?.offerId ?? evaluation.topOfferId),
  );
  const dossierComplete = Boolean(
    evaluation && selectedOffer && evaluation.recommendation,
  );
  const requestStage = procurementStageOf(request);
  const gmApproval = request.approvalChain?.find(
    (approval) => approval.role === "GENEL_MUDUR",
  );
  const managerApproval = request.approvalChain?.find(
    (approval) => approval.role === "SATIN_ALMA_MUDURU",
  );
  const managerSelectedOffer = evaluation?.offers.find(
    (offer) => offer.id === managerApproval?.decidedOfferId,
  );
  const rankedOffers = evaluation
    ? [...evaluation.offers].sort((left, right) => {
      const leftRank = left.rank ?? Number.POSITIVE_INFINITY;
      const rightRank = right.rank ?? Number.POSITIVE_INFINITY;
      if (leftRank !== rightRank) return leftRank - rightRank;
      const leftScore =
        left.totalScore ?? Number.NEGATIVE_INFINITY;
      const rightScore =
        right.totalScore ?? Number.NEGATIVE_INFINITY;
      if (leftScore !== rightScore) return rightScore - leftScore;
      return left.supplier.localeCompare(right.supplier, "tr");
    })
    : [];
  const finiteMetricValues = (
    values: Array<number | null | undefined>,
  ) =>
    values.filter(
      (value): value is number =>
        typeof value === "number" && Number.isFinite(value),
    );
  const unitPrices = finiteMetricValues(
    rankedOffers.map((offer) => offer.unitPrice),
  );
  const deliveryDays = finiteMetricValues(
    rankedOffers.map((offer) => offer.deliveryDays),
  );
  const paymentDays = finiteMetricValues(
    rankedOffers.map((offer) => offer.paymentDays),
  );
  const bestUnitPrice = unitPrices.length
    ? Math.min(...unitPrices)
    : null;
  const bestDeliveryDays = deliveryDays.length
    ? Math.min(...deliveryDays)
    : null;
  const bestPaymentDays = paymentDays.length
    ? Math.max(...paymentDays)
    : null;
  const persistedFinalOffer = evaluation?.offers.find(
    (offer) => offer.id === gmApproval?.decidedOfferId,
  );
  const isCompleted = requestStage === "Tamamlandı";
  const isRejected = requestStage === "Reddedildi";
  const finalSelectionChanged =
    gmApproval?.decisionKind === "GM_FINAL_OVERRIDE";
  const pendingHumanOffer =
    request.pendingApprovalRole === "GENEL_MUDUR" &&
      managerSelectedOffer
      ? managerSelectedOffer
      : selectedOffer;
  const displayedOffer =
    isCompleted && persistedFinalOffer
      ? persistedFinalOffer
      : pendingHumanOffer;
  const displayedDiffersFromSystem = Boolean(
    displayedOffer &&
    systemOffer &&
    displayedOffer.id !== systemOffer.id,
  );
  const selectionLabel = isCompleted
    ? "Nihai seçim"
    : request.pendingApprovalRole === "GENEL_MUDUR" &&
      managerSelectedOffer
      ? "Müdür önerisi"
      : "Uzman tercihi";
  const selectionOwner = isCompleted
    ? (gmApproval?.decidedBy ?? "Genel Müdür")
    : request.pendingApprovalRole === "GENEL_MUDUR" &&
      managerApproval
      ? (managerApproval.decidedBy ?? "Satın Alma Müdürü")
      : (evaluation?.recommendation?.createdByName ??
        "Henüz kaydedilmedi");
  const gmFinalOfferId =
    gmFinalOfferSelections[request.id] ?? selectedOffer?.id ?? "";
  const gmFinalOffer = evaluation?.offers.find(
    (offer) => offer.id === gmFinalOfferId,
  );
  const gmChangedManagerSelection = Boolean(
    user.role === "GENEL_MUDUR" &&
    selectedOffer &&
    gmFinalOffer &&
    gmFinalOffer.id !== selectedOffer.id,
  );
  const gmFinalDecisionNote =
    gmFinalDecisionNotes[request.id] ?? "";
  const missingDossierItems = !evaluation
    ? ["Teklif değerlendirmesi tamamlanmadı"]
    : [
      !selectedOffer ? "Uzman tarafından teklif seçilmedi" : null,
      !evaluation.recommendation
        ? "Uzman kararı ve gerekçesi kaydedilmedi"
        : null,
    ].filter((item): item is string => Boolean(item));
  const isExpanded = expandedRequestId === request.id;
  const attentionLabel = isCompleted
    ? "Nihai karar verildi"
    : isRejected
      ? "Düzeltmeye gönderildi"
      : !dossierComplete
        ? "Eksik bilgi"
        : request.pendingApprovalRole === "GENEL_MUDUR"
          ? "Nihai karar bekliyor"
          : request.pendingApprovalRole === "SATIN_ALMA_MUDURU"
            ? user.role === "SATIN_ALMA_MUDURU"
              ? "Onayınız bekleniyor"
              : "Müdür onayı bekliyor"
            : displayedDiffersFromSystem
              ? "Gerekçeli seçim"
              : "Dosya hazır";
  const attentionDetail = isCompleted
    ? `${selectionOwner} tarafından nihai tedarikçi kararı kaydedildi.`
    : isRejected
      ? "Dosya düzeltme ve yeniden değerlendirme için Satın Alma Müdürüne gönderildi."
      : !dossierComplete
        ? `Eksik: ${missingDossierItems.join(" · ")}`
        : request.pendingApprovalRole === "GENEL_MUDUR"
          ? "Satın Alma Müdürü önerisini iletti; Genel Müdürün bağlayıcı kararı bekleniyor."
          : request.pendingApprovalRole === "SATIN_ALMA_MUDURU"
            ? user.role === "SATIN_ALMA_MUDURU"
              ? "Uzman değerlendirmesi tamamlandı; onayınız bekleniyor."
              : "Uzman değerlendirmesi tamamlandı; Satın Alma Müdürü kararı bekleniyor."
            : displayedDiffersFromSystem
              ? "Sistem önerisinden farklı teklif gerekçesiyle birlikte kaydedildi."
              : "Dosya bir sonraki karar adımı için hazır.";
  const requiresAttention =
    isRejected || (!isCompleted && !dossierComplete);
  return { ...model, request, resolution, isActionable, evaluation, systemOffer, selectedOffer, dossierComplete, gmApproval, managerApproval, managerSelectedOffer, rankedOffers, deliveryDays, paymentDays, bestUnitPrice, bestDeliveryDays, bestPaymentDays, isCompleted, finalSelectionChanged, displayedOffer, displayedDiffersFromSystem, selectionLabel, selectionOwner, gmFinalOfferId, gmFinalOffer, gmChangedManagerSelection, gmFinalDecisionNote, isExpanded, attentionLabel, attentionDetail, requiresAttention };
}
export type ApprovalRowContext = ReturnType<typeof createApprovalRowModel<ApprovalsWorkspace>>;
